import { buttonVariants } from "./button.styles";
import { cva, cx, type VariantProps } from "./style.utils";

export type ToastIconVariants = VariantProps<typeof toastIconVariants>;

export const toastIconVariants = cva({
  variants: {
    intent: {
      error: "text-error-100",
      warning: "text-warning-120",
      success: "text-success-120",
      info: "text-info-120",
      help: "text-neutral-120",
      loading: "animate-spin text-link-hover",
    },
  },
  defaultVariants: {
    intent: "help",
  },
});

/*
General structure of a toast
with all possible elements
and configurable prop keys:

li (classNames.toast)
├── button (classNames.closeButton)
│   └── svg
├── div (classNames.icon)
│   ├── div (classNames.loader)
│   │   └── svg
│   └── svg
├── div (classNames.content)
│   ├── div (classNames.title)
│   └── div (classNames.description)
├── button (classNames.cancelButton)
└── button (classNames.actionButton)
*/

// Parent element <li>
export const toastClassNames = cx(
  // layout
  "grid w-full grid-flow-row grid-cols-alert-callout overflow-hidden px-15px py-16px",
  // radii/shadow
  "rounded-l rounded-r-md shadow-lg",
  // border
  "border-x-4 border-r-transparent",
  // foreground
  "font-sans text-sm text-shade-100",
  // close button unset hover
  "hover:data-[close-button]:*:bg-black/5",

  // default border
  "border-l-neutral-100",
  // default background
  "bg-neutral-20",
  // default close button
  "data-[close-button]:*:text-neutral-120",

  // success border
  "data-[type=success]:border-l-success-100",
  // success background
  "data-[type=success]:bg-success-20",
  // success close button
  "data-[close-button]:*:data-[type=success]:text-success-120",

  // info border
  "data-[type=info]:border-l-info-100",
  // info background
  "data-[type=info]:bg-info-20",
  // info close button
  "data-[close-button]:*:data-[type=info]:text-info-120",

  // warning border
  "data-[type=warning]:border-l-warning-100",
  // warning background
  "data-[type=warning]:bg-warning-20",
  // warning close button
  "data-[close-button]:*:data-[type=warning]:text-warning-120",

  // error border
  "data-[type=error]:border-l-error-100",
  // error background
  "data-[type=error]:bg-error-20",
  // error button close button
  "data-[close-button]:*:data-[type=error]:text-error-100",
);

// 1st child
// li > button[data-close-button]
export const closeButtonClassNames = cx(
  // layout unset
  "static size-30px rounded-md border-none bg-transparent p-5px *:size-full focus-visible:shadow-none",
  // layout
  "row-start-1 col-start-close-s col-end-close-e",
  // layout towards corner
  "-mb-10px -translate-y-5px translate-x-8px",
);

// 2nd child
// li > div[data-icon]
export const iconClassNames = cx(
  // layout unset
  "m-0 size-20px *:m-0",
  // layout
  "row-start-1 col-start-icon-s col-end-icon-e",
);
// li > div[data-icon] > div[data-loader]
export const loaderClassNames = cx(
  // layout unset
  "data-[visible=false]:!left-0",
  "data-[visible=true]:!left-24px",
);

// 3rd child
// li > div[data-content]
export const contentClassNames = cx(
  // layout
  "row-start-1 col-start-s col-end-content-e",
  // layout variants
  "[[data-type=success]_&]:col-start-content-s",
  "[[data-type=info]_&]:col-start-content-s",
  "[[data-type=warning]_&]:col-start-content-s",
  "[[data-type=error]_&]:col-start-content-s",
  "[[data-type=loading]_&]:col-start-content-s",
  // layout children
  "space-y-5px",
);
// li > div[data-content] > div[data-title]
export const titleClassNames = cx("font-semibold last:font-normal");
// li > div[data-content] > div[data-description]
export const descriptionClassNames = cx();

// 4th child
// li > button[data-cancel]
export const cancelButtonClassNames = buttonVariants({
  variant: "outline",
  palette: "accent",
  size: "sm",
  className: "col-start-content-s col-end-content-e mt-15px",
});

// 5th child
// li > button[data-action]
export const actionButtonClassNames = buttonVariants({
  variant: "solid",
  palette: "accent",
  size: "sm",
  className: "col-start-content-s col-end-content-e mt-10px",
});
